import {formatDate, formatEuro, lowMarginHint, statusPresentation} from '../../core/billing/billing.core.js';
import {escapeHtml} from '../../core/escape-html.js';
import {renewalText, storedRenewal} from '../../core/billing/client-invoice.core.js';

/**
 * @param {number} months
 * @returns {string} e.g. `Every 12 months`, `Every month`
 */
export function intervalText(months) {
    return months === 1 ? 'Every month' : `Every ${months} months`;
}

/**
 * @param {import('../../shell/types/billing.types.js').BillingItemApiResponse} item
 * @param {boolean} [expanded] whether the history panel is open
 * @returns {string} inner markup of one `.billing-item`: the item's details, its action buttons
 *   (Record invoice only for an active item, since an inactive one cannot be invoiced) and an empty
 *   `.billing-item-extra` history panel below them
 */
function billingItemMarkup(item, expanded = false) {
    const status = statusPresentation(item.status);
    const label = escapeHtml(item.label);
    const hint = lowMarginHint(item);
    const lowMargin = item.lowMargin
        ? `<span class="low-margin" title="${hint}">
                <i aria-hidden="true" class="fa-solid fa-triangle-exclamation"></i>
                <span class="visually-hidden">${hint}</span>
            </span>`
        : '';
    // The warning sits next to the figure it judges: the last invoice once there is one, else the price.
    const lastInvoice = item.lastInvoice
        ? `<span class="billing-last-invoice">${lowMargin}Last invoice ${escapeHtml(formatEuro(item.lastInvoice.amount))}</span>`
        : '';
    const priceWarning = item.lastInvoice ? '' : lowMargin;
    const invoicedButton = item.active
        ? `<button type="button" class="btn btn-soft btn-sm btn-billing-invoiced" data-id="${item.id}" aria-label="Record an invoice for ${label}">
                            <i aria-hidden="true" class="fa-solid fa-file-invoice"></i> Record invoice
                        </button>`
        : '';
    const supplier = item.supplier ? `<span class="billing-supplier">${escapeHtml(item.supplier)}</span>` : '';
    return `
                <div class="billing-item-main">
                    <div class="billing-item-text">
                        <span class="billing-label">${label}</span>
                        <span class="billing-meta">
                            ${supplier}
                            <span class="billing-interval">${escapeHtml(intervalText(item.intervalMonths))}</span>
                            <span class="billing-paid-until">Paid until ${escapeHtml(formatDate(item.paidUntil))}</span>
                            ${lastInvoice}
                        </span>
                    </div>
                    <div class="billing-item-figures">
                        <span class="billing-price">${priceWarning}${escapeHtml(formatEuro(item.price))}</span>
                        <span class="badge ${status.badgeClass}">${escapeHtml(status.label)}</span>
                    </div>
                    <div class="billing-item-actions">
                        ${invoicedButton}
                        <button type="button" class="btn btn-outline btn-sm btn-billing-history" data-id="${item.id}" aria-expanded="${expanded}" aria-controls="billing-history-${item.id}" aria-label="Invoice history of ${label}">
                            <i aria-hidden="true" class="fa-solid fa-clock-rotate-left"></i> History
                        </button>
                        <button type="button" class="btn btn-outline btn-sm btn-billing-edit" data-id="${item.id}" aria-label="Edit billing item ${label}">
                            <i aria-hidden="true" class="fa-solid fa-pen-to-square"></i> Edit
                        </button>
                        <button type="button" class="btn btn-delete btn-sm btn-billing-delete" data-id="${item.id}" aria-label="Delete billing item ${label}">
                            <i aria-hidden="true" class="fa-solid fa-trash-can"></i> Delete
                        </button>
                    </div>
                </div>
                <div class="billing-item-extra" id="billing-history-${item.id}" data-id="${item.id}" ${expanded ? '' : 'hidden'}></div>
            `;
}

/**
 * Renders the billing list into `container`, replacing its content: an empty state with an add
 * action when the client has no items, otherwise the client's renewal date and one `.billing-item` per item. Inactive items get
 * `is-inactive`; the item open in the form gets `is-editing`; items in `expandedIds` show their history panel.
 *
 * @param {HTMLElement} container
 * @param {import('../../shell/types/billing.types.js').BillingItemApiResponse[]} items
 * @param {{editingId?: number | null, expandedIds?: Set<number>}} [options]
 */
export function renderBillingCard(container, items, {editingId = null, expandedIds = new Set()} = {}) {
    container.innerHTML = '';

    if (items.length === 0) {
        const empty = document.createElement('empty-state');
        const title = document.createElement('span');
        title.slot = 'title';
        title.textContent = 'No recurring billing yet';
        const add = document.createElement('button');
        add.type = 'button';
        add.slot = 'cta';
        add.className = 'btn btn-primary btn-sm btn-billing-add';
        add.textContent = 'Add item';
        empty.append(title, 'Track domains, hosting and other items you invoice every period.', add);
        container.appendChild(empty);
        return;
    }

    const renewal = storedRenewal(items);
    const renewalLine = document.createElement('p');
    renewalLine.className = 'billing-renewal';
    renewalLine.textContent = renewal ? `Renewal date: ${renewalText(renewal)}` : 'No renewal date yet, set when you invoice this client in Billing';
    container.appendChild(renewalLine);

    for (const item of items) {
        const row = document.createElement('div');
        row.className = 'billing-item';
        row.dataset.id = String(item.id);
        row.innerHTML = billingItemMarkup(item, expandedIds.has(item.id));
        if (!item.active) {
            row.classList.add('is-inactive');
        }
        if (item.id === editingId) {
            row.classList.add('is-editing');
            row.setAttribute('aria-current', 'true');
        }
        container.appendChild(row);
    }
}
